import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, CheckCircle2, XCircle, Clock, AlertCircle, Eye, Download, Edit, Link2, Plus } from 'lucide-react';
import { aiPurchasesAPI, suppliersAPI } from '../../api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { RotatingGlowButton } from '../../components/ui/RotatingGlowButton';
import { Card, CardContent } from '../../components/ui/Card';
import { useToast } from '../../hooks/useToast';
import SupplierPicker from '../../components/SupplierPicker';
import { SlideOver } from '../../components/ui/SlideOver';
import { Input } from '../../components/ui/Input';

const PurchaseAIReview = () => {
  const navigate = useNavigate();
  const { success, error, ToastContainer } = useToast();

  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [showSupplierPicker, setShowSupplierPicker] = useState(false);
  const [showCreateSupplier, setShowCreateSupplier] = useState(false);
  const [supplierFormData, setSupplierFormData] = useState({});
  const [creatingSupplier, setCreatingSupplier] = useState(false);

  useEffect(() => {
    loadDrafts();
  }, [statusFilter, pagination.page]);

  const loadDrafts = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const result = await aiPurchasesAPI.getDrafts(params);
      setDrafts(result.drafts || []);
      setPagination(prev => ({
        ...prev,
        total: result.pagination?.total || 0,
        totalPages: result.pagination?.totalPages || 0,
      }));
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Failed to load drafts');
    } finally {
      setLoading(false);
    }
  };

  const loadDraftDetails = async (id) => {
    try {
      const result = await aiPurchasesAPI.getDraft(id);
      setSelectedDraft(result.draft);
      
      // Pre-populate supplier form with extracted data if available
      const extracted = result.draft?.extracted_data ? 
        (typeof result.draft.extracted_data === 'string' 
          ? JSON.parse(result.draft.extracted_data) 
          : result.draft.extracted_data) 
        : null;
      
      if (extracted?.supplier_name) {
        setSupplierFormData({
          supplier_name: extracted.supplier_name || '',
          supplier_code: extracted.supplier_code || '',
          contact_name: extracted.supplier_contact_name || extracted.contact_name || '',
          email: extracted.supplier_email || extracted.email || '',
          phone: extracted.supplier_phone || extracted.phone || '',
          address_line1: extracted.supplier_address_line1 || extracted.address_line1 || '',
          address_line2: extracted.supplier_address_line2 || extracted.address_line2 || '',
          city: extracted.supplier_city || extracted.city || '',
          county: extracted.supplier_county || extracted.county || '',
          postcode: extracted.supplier_postcode || extracted.postcode || '',
          country: extracted.supplier_country || extracted.country || 'GB',
          payment_terms: extracted.payment_terms || '',
        });
      }
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Failed to load draft details');
    }
  };

  const handleApprove = async (id) => {
    if (!confirm('Are you sure you want to approve this draft and create a purchase?')) {
      return;
    }
    try {
      await aiPurchasesAPI.approve(id);
      success('Draft approved and purchase created successfully!');
      loadDrafts();
      setSelectedDraft(null);
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Failed to approve draft');
    }
  };

  const handleReject = async (id) => {
    const notes = prompt('Enter rejection notes (optional):');
    if (notes === null) return; // User cancelled
    
    try {
      await aiPurchasesAPI.reject(id, notes || '');
      success('Draft rejected successfully');
      loadDrafts();
      setSelectedDraft(null);
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Failed to reject draft');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'needs_review':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'approved':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200';
      case 'needs_review':
        return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200';
      case 'failed':
      case 'rejected':
        return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200';
      case 'processing':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSupplierSelect = async (supplier) => {
    if (!selectedDraft) return;
    
    try {
      await aiPurchasesAPI.update(selectedDraft.id, { supplier_id: supplier.id });
      success(`Supplier "${supplier.supplier_name}" linked to draft`);
      setShowSupplierPicker(false);
      loadDraftDetails(selectedDraft.id); // Reload to get updated draft
    } catch (err) {
      error(err.response?.data?.error || err.message || 'Failed to link supplier');
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    
    if (!supplierFormData.supplier_name?.trim()) {
      error('Supplier name is required');
      return;
    }

    setCreatingSupplier(true);
    try {
      const response = await suppliersAPI.create(supplierFormData);
      const newSupplier = response.supplier || response;
      
      // Link the new supplier to the draft
      await aiPurchasesAPI.update(selectedDraft.id, { supplier_id: newSupplier.id });
      
      success(`Supplier "${newSupplier.supplier_name}" created and linked to draft`);
      setShowCreateSupplier(false);
      loadDraftDetails(selectedDraft.id); // Reload to get updated draft
    } catch (err) {
      error(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to create supplier');
    } finally {
      setCreatingSupplier(false);
    }
  };

  const handleSupplierFormChange = (e) => {
    const { name, value } = e.target;
    setSupplierFormData(prev => ({ ...prev, [name]: value }));
  };

  const extractedData = selectedDraft?.extracted_data ? 
    (typeof selectedDraft.extracted_data === 'string' 
      ? JSON.parse(selectedDraft.extracted_data) 
      : selectedDraft.extracted_data) 
    : null;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-h1 font-medium text-[var(--ds-text-primary)] mb-2">
              AI Purchase Drafts Review
            </h1>
            <p className="text-body text-[var(--ds-text-secondary)]">
              Review and approve AI-extracted purchase invoices
            </p>
          </div>
          <RotatingGlowButton
            variant="secondary"
            size="sm"
            onClick={() => navigate('/tenant/purchases/ai-upload')}
            className="inline-flex items-center gap-2 text-orange-500"
            glowColor="orange"
            glowSpeed={1}
          >
            Upload New Invoice
          </RotatingGlowButton>
        </div>

        {/* Status Filter */}
        <div className="mb-6 flex gap-2 flex-wrap">
          {['all', 'processing', 'completed', 'needs_review', 'failed', 'approved', 'rejected'].map(status => (
            <Button
              key={status}
              variant={statusFilter === status ? 'primary' : 'outline'}
              size="sm"
              onClick={() => {
                setStatusFilter(status);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Button>
          ))}
        </div>

        {loading ? (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-body text-[var(--ds-text-secondary)]">Loading drafts...</p>
            </CardContent>
          </Card>
        ) : drafts.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-[var(--ds-text-secondary)]" />
              <p className="text-body text-[var(--ds-text-secondary)] mb-4">
                No drafts found{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.
              </p>
              <RotatingGlowButton
                onClick={() => navigate('/tenant/purchases/ai-upload')}
                glowColor="orange"
                glowSpeed={1}
                className="text-orange-500"
              >
                Upload Your First Invoice
              </RotatingGlowButton>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Drafts List */}
            <div className="lg:col-span-2 space-y-4">
              {drafts.map(draft => (
                <Card key={draft.id} className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => loadDraftDetails(draft.id)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusIcon(draft.status)}
                          <h3 className="text-h3 font-medium text-[var(--ds-text-primary)]">
                            {draft.source_file_name}
                          </h3>
                        </div>
                        <div className="flex items-center gap-4 text-meta text-[var(--ds-text-secondary)] mb-2">
                          <span className={`px-2 py-1 rounded ${getStatusColor(draft.status)}`}>
                            {draft.status.replace('_', ' ')}
                          </span>
                          {draft.ai_confidence && (
                            <span>Confidence: {Math.round(draft.ai_confidence * 100)}%</span>
                          )}
                        </div>
                        {draft.supplier_name && (
                          <p className="text-body text-[var(--ds-text-primary)] mb-1">
                            Supplier: <strong>{draft.supplier_name}</strong>
                          </p>
                        )}
                        <p className="text-meta text-[var(--ds-text-secondary)]">
                          Uploaded {formatDate(draft.created_at)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          loadDraftDetails(draft.id);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </Button>
                  <span className="text-body text-[var(--ds-text-secondary)]">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>

            {/* Draft Details Sidebar */}
            {selectedDraft && (
              <div className="lg:col-span-1">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-h2 font-medium text-[var(--ds-text-primary)]">
                        Draft Details
                      </h2>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedDraft(null)}
                      >
                        ×
                      </Button>
                    </div>

                    {extractedData && (
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-h3 font-medium text-[var(--ds-text-primary)] mb-2">
                            Extracted Data
                          </h3>
                          <div className="bg-[var(--ds-control-field-bg)] rounded-[var(--ds-radius-md)] p-4 space-y-2">
                            {extractedData.supplier_name && (
                              <div className="space-y-2">
                                <div>
                                  <span className="text-meta text-[var(--ds-text-secondary)]">Extracted Supplier: </span>
                                  <span className="text-body text-[var(--ds-text-primary)] font-medium">
                                    {extractedData.supplier_name}
                                  </span>
                                </div>
                                {selectedDraft.supplier_id ? (
                                  <div className="space-y-2 pt-2 border-t border-[var(--ds-border-subtle)]">
                                    <div className="flex items-center gap-2 text-xs">
                                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                                      <span className="text-[var(--ds-text-secondary)]">
                                        Matched to: <strong className="text-[var(--ds-text-primary)]">{selectedDraft.supplier_name || 'Supplier'}</strong>
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setShowSupplierPicker(true)}
                                        className="flex-1"
                                      >
                                        <Link2 className="w-3 h-3 mr-1" />
                                        Change Match
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setShowCreateSupplier(true)}
                                        className="flex-1"
                                      >
                                        <Plus className="w-3 h-3 mr-1" />
                                        Create New
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 pt-2 border-t border-[var(--ds-border-subtle)]">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setShowSupplierPicker(true)}
                                      className="flex-1"
                                    >
                                      <Link2 className="w-3 h-3 mr-1" />
                                      Match Supplier
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setShowCreateSupplier(true)}
                                      className="flex-1"
                                    >
                                      <Plus className="w-3 h-3 mr-1" />
                                      Create Supplier
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                            {extractedData.invoice_number && (
                              <div>
                                <span className="text-meta text-[var(--ds-text-secondary)]">Invoice #: </span>
                                <span className="text-body text-[var(--ds-text-primary)]">
                                  {extractedData.invoice_number}
                                </span>
                              </div>
                            )}
                            {extractedData.purchase_date && (
                              <div>
                                <span className="text-meta text-[var(--ds-text-secondary)]">Date: </span>
                                <span className="text-body text-[var(--ds-text-primary)]">
                                  {extractedData.purchase_date}
                                </span>
                              </div>
                            )}
                            {extractedData.total && (
                              <div>
                                <span className="text-meta text-[var(--ds-text-secondary)]">Total: </span>
                                <span className="text-body text-[var(--ds-text-primary)] font-medium">
                                  £{parseFloat(extractedData.total).toFixed(2)}
                                </span>
                              </div>
                            )}
                            {extractedData.items && extractedData.items.length > 0 && (
                              <div className="mt-3">
                                <span className="text-meta text-[var(--ds-text-secondary)]">Items: </span>
                                <span className="text-body text-[var(--ds-text-primary)]">
                                  {extractedData.items.length} line item(s)
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {extractedData.items && extractedData.items.length > 0 && (
                          <div>
                            <h4 className="text-h4 font-medium text-[var(--ds-text-primary)] mb-2">
                              Line Items
                            </h4>
                            <div className="bg-[var(--ds-control-field-bg)] rounded-[var(--ds-radius-md)] p-4">
                              <div className="space-y-2">
                                {extractedData.items.map((item, idx) => (
                                  <div key={idx} className="border-b border-[var(--ds-border-subtle)] pb-2 last:border-0">
                                    <p className="text-body text-[var(--ds-text-primary)] font-medium">
                                      {item.description}
                                    </p>
                                    <p className="text-meta text-[var(--ds-text-secondary)]">
                                      Qty: {item.quantity} × £{parseFloat(item.unit_price || 0).toFixed(2)}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedDraft.error_message && (
                      <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/20 rounded text-red-800 dark:text-red-200">
                        <p className="text-body font-medium mb-1">Error:</p>
                        <p className="text-meta">{selectedDraft.error_message}</p>
                      </div>
                    )}

                    <div className="mt-6 flex gap-2">
                      {selectedDraft.status === 'completed' || selectedDraft.status === 'needs_review' ? (
                        <>
                          <Button
                            onClick={() => handleApprove(selectedDraft.id)}
                            className="flex-1"
                            variant="primary"
                            size="sm"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleReject(selectedDraft.id)}
                            variant="outline"
                            size="sm"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </>
                      ) : null}
                    </div>

                    <div className="mt-4 flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.open(`/api/ai-purchases/drafts/${selectedDraft.id}/file`, '_blank');
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download File
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>
      <ToastContainer />

      {/* Supplier Picker Modal */}
      {showSupplierPicker && (
        <SupplierPicker
          onSelect={handleSupplierSelect}
          onClose={() => setShowSupplierPicker(false)}
          showModal={true}
        />
      )}

      {/* Create Supplier Modal */}
      {showCreateSupplier && (
        <SlideOver
          open={showCreateSupplier}
          onOpenChange={setShowCreateSupplier}
          title="Create New Supplier"
          width="w-full max-w-2xl"
        >
          <form onSubmit={handleCreateSupplier} className="space-y-4">
            <div>
              <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                Supplier Name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="supplier_name"
                value={supplierFormData.supplier_name || ''}
                onChange={handleSupplierFormChange}
                required
                placeholder="ABC Electrical Supplies Ltd"
                className="h-8"
              />
            </div>

            <div>
              <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                Supplier Code
              </label>
              <Input
                type="text"
                name="supplier_code"
                value={supplierFormData.supplier_code || ''}
                onChange={handleSupplierFormChange}
                placeholder="ABC-001"
                className="h-8"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact Name
                </label>
                <Input
                  type="text"
                  name="contact_name"
                  value={supplierFormData.contact_name || ''}
                  onChange={handleSupplierFormChange}
                  placeholder="John Smith"
                  className="h-8"
                />
              </div>
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <Input
                  type="email"
                  name="email"
                  value={supplierFormData.email || ''}
                  onChange={handleSupplierFormChange}
                  placeholder="supplier@example.com"
                  className="h-8"
                />
              </div>
            </div>

            <div>
              <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone
              </label>
              <Input
                type="tel"
                name="phone"
                value={supplierFormData.phone || ''}
                onChange={handleSupplierFormChange}
                placeholder="+44 20 1234 5678"
                className="h-8"
              />
            </div>

            <div>
              <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                Address Line 1
              </label>
              <Input
                type="text"
                name="address_line1"
                value={supplierFormData.address_line1 || ''}
                onChange={handleSupplierFormChange}
                placeholder="123 High Street"
                className="h-8"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  City
                </label>
                <Input
                  type="text"
                  name="city"
                  value={supplierFormData.city || ''}
                  onChange={handleSupplierFormChange}
                  placeholder="London"
                  className="h-8"
                />
              </div>
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Postcode
                </label>
                <Input
                  type="text"
                  name="postcode"
                  value={supplierFormData.postcode || ''}
                  onChange={handleSupplierFormChange}
                  placeholder="SW1A 1AA"
                  className="h-8"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-[var(--ds-border-divider)]">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCreateSupplier(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={creatingSupplier}
              >
                {creatingSupplier ? 'Creating...' : 'Create & Link Supplier'}
              </Button>
            </div>
          </form>
        </SlideOver>
      )}
    </Layout>
  );
};

export default PurchaseAIReview;
